import React, { useEffect, useRef, useState } from 'react';
import { Calendar, Clock } from 'lucide-react';

/**
 * Lightweight DD/MM/YYYY masked input — no library dependency.
 *
 * PROPS:
 *   value    → stored in ISO YYYY-MM-DD format (what the backend expects)
 *   onChange → called with an ISO YYYY-MM-DD string when the user has
 *              typed a fully-valid date, or '' otherwise
 *   testId, name, id, className
 *
 * The visible input always shows DD/MM/YYYY (Indian mobile-friendly),
 * uses inputMode="numeric" so the mobile keypad opens, auto-inserts
 * slashes as the user types, and validates ranges (day 1-31, month 1-12,
 * year 1900-current).
 */
export const MaskedDateInput = ({
  value = '',
  onChange,
  testId,
  id,
  className = '',
  required = false,
  placeholder = 'DD/MM/YYYY',
}) => {
  const [display, setDisplay] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Sync display when parent-provided ISO value changes
  useEffect(() => {
    if (!value) { setDisplay(''); return; }
    // value comes in as YYYY-MM-DD → convert to DD/MM/YYYY
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (m) setDisplay(`${m[3]}/${m[2]}/${m[1]}`);
  }, [value]);

  const emitISO = (dd, mm, yyyy) => {
    if (!onChange) return;
    if (dd && mm && yyyy && yyyy.length === 4) {
      onChange(`${yyyy}-${mm}-${dd}`);
    } else {
      onChange('');
    }
  };

  const handleChange = (e) => {
    // Strip everything except digits; take max 8 digits (DDMMYYYY)
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);

    let dd = digits.slice(0, 2);
    let mm = digits.slice(2, 4);
    let yyyy = digits.slice(4, 8);

    // Live-validate day (only if 2 digits typed)
    if (dd.length === 2) {
      const d = parseInt(dd, 10);
      if (d < 1) dd = '01'; else if (d > 31) dd = '31';
    }
    // Live-validate month
    if (mm.length === 2) {
      const m = parseInt(mm, 10);
      if (m < 1) mm = '01'; else if (m > 12) mm = '12';
    }

    // Reassemble display with slashes
    let out = dd;
    if (digits.length >= 2) out = `${dd}/`;
    if (digits.length >= 3) out += mm;
    if (digits.length >= 4) out = `${dd}/${mm}/`;
    if (digits.length >= 5) out += yyyy;
    setDisplay(out);

    // Final validity check
    const nowY = new Date().getFullYear();
    if (dd.length === 2 && mm.length === 2 && yyyy.length === 4) {
      const y = parseInt(yyyy, 10);
      if (y < 1900 || y > nowY) {
        setError(`Year must be 1900–${nowY}`);
        emitISO(null); return;
      }
      // Verify real calendar date (e.g. reject 31/02/2000)
      const iso = `${yyyy}-${mm}-${dd}`;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== iso) {
        setError('Please enter a valid date');
        emitISO(null); return;
      }
      setError('');
      emitISO(dd, mm, yyyy);
    } else {
      setError('');
      emitISO(null);
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="bday"
        data-testid={testId}
        value={display}
        onChange={handleChange}
        placeholder={placeholder}
        maxLength={10}
        required={required}
        className={`w-full pl-11 pr-3 h-12 rounded-xl bg-white border-2 border-brand-gold/30 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none text-brand-text-dark placeholder:text-brand-text-mute tracking-wider font-mono text-base ${error ? '!border-red-400' : ''} ${className}`}
      />
      <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-gold-dark pointer-events-none" />
      {error && (
        <p className="text-xs text-red-600 mt-1 ml-1">{error}</p>
      )}
    </div>
  );
};

/**
 * Lightweight HH:MM 24-hour masked input — no library.
 *
 * Stores value as HH:MM (backend-friendly). Auto-inserts colon, validates
 * hour 0-23 and minute 0-59. Uses inputMode="numeric" for mobile keypad.
 */
export const MaskedTimeInput = ({
  value = '',
  onChange,
  testId,
  id,
  className = '',
  required = false,
  placeholder = 'HH:MM (24-hour)',
}) => {
  const [display, setDisplay] = useState(value || '');
  const [error, setError] = useState('');

  useEffect(() => { setDisplay(value || ''); }, [value]);

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
    let hh = digits.slice(0, 2);
    let mm = digits.slice(2, 4);
    if (hh.length === 2) {
      const h = parseInt(hh, 10);
      if (h > 23) hh = '23';
    }
    if (mm.length === 2) {
      const m = parseInt(mm, 10);
      if (m > 59) mm = '59';
    }
    let out = hh;
    if (digits.length >= 2) out = `${hh}:`;
    if (digits.length >= 3) out += mm;
    setDisplay(out);

    if (hh.length === 2 && mm.length === 2) {
      setError('');
      onChange && onChange(`${hh}:${mm}`);
    } else {
      setError('');
      onChange && onChange('');
    }
  };

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        inputMode="numeric"
        data-testid={testId}
        value={display}
        onChange={handleChange}
        placeholder={placeholder}
        maxLength={5}
        required={required}
        className={`w-full pl-11 pr-3 h-12 rounded-xl bg-white border-2 border-brand-gold/30 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none text-brand-text-dark placeholder:text-brand-text-mute tracking-wider font-mono text-base ${error ? '!border-red-400' : ''} ${className}`}
      />
      <Clock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-gold-dark pointer-events-none" />
      {error && (
        <p className="text-xs text-red-600 mt-1 ml-1">{error}</p>
      )}
    </div>
  );
};
